import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useEffect, useState, useMemo } from "react";
import { Eye, User, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

interface MetricsSceneProps {
  isActive: boolean;
}

// Chart data points
const CHART_DATA = [
  { date: "26/12", get label() { return translateInterfaceText("26 DEZ 2025"); }, totais: 80, unicas: 63, playRate: 28, tempo: 33 },
  { date: "02/01", get label() { return translateInterfaceText("02 JAN 2026"); }, totais: 45, unicas: 31, playRate: 22, tempo: 28 },
  { date: "09/01", get label() { return translateInterfaceText("09 JAN 2026"); }, totais: 74, unicas: null, playRate: 29, tempo: 33 },
  { date: "16/01", get label() { return translateInterfaceText("16 JAN 2026"); }, totais: 68, unicas: 51, playRate: 31, tempo: 41 },
  { date: "23/01", get label() { return translateInterfaceText("23 JAN 2026"); }, totais: 77, unicas: 59, playRate: 35, tempo: 46 },
];

// KPI states that change as cursor moves
const KPI_STATES = [
  { visualizacoes: 1742, unicas: 1432, playRate: 28.2 },
  { visualizacoes: 1787, unicas: 1463, playRate: 25.1 },
  { visualizacoes: 1861, unicas: 1463, playRate: 29.4 },
  { visualizacoes: 1929, unicas: 1514, playRate: 30.8 },
  { visualizacoes: 2006, unicas: 1573, playRate: 32.7 },
];

// Animation timing (in ms) - Optimized for faster cycle
const POINT_PAUSE = 1400;  // Reduced from 2200
const MOVE_DURATION = 500; // Reduced from 800
const LOOP_DELAY = 400;    // Reduced from 600

// Only animate these 2 points (skip first point which causes tooltip cutoff)
const ANIMATION_POINTS = [1, 3]; // 02/01 and 16/01

// Count-up hook
function useCountUp(target: number, duration: number, trigger: number) {
  useLanguage();
  const [value, setValue] = useState(target);
  const [prevTarget, setPrevTarget] = useState(target);

  useEffect(() => {
    if (target === prevTarget) return;
    
    const startValue = prevTarget;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(startValue + (target - startValue) * eased);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setPrevTarget(target);
      }
    };

    requestAnimationFrame(animate);
  }, [target, prevTarget, duration, trigger]);

  return value;
}

// Format number with thousand separator
function formatNumber(num: number): string {
  return Math.round(num).toLocaleString(getInterfaceLanguage());
}

// Mini tabs component
function MiniTabs() {
  useLanguage();
  return (
    <div className="flex items-center gap-1 mb-3">
      <span className="px-2 py-0.5 text-[10px] font-medium bg-[#1F6BFF] text-white rounded">
        {translateInterfaceText("Visualizações")}</span>
      <span className="px-2 py-0.5 text-[10px] font-medium text-gray-400 hover:text-gray-600 cursor-default">
        {translateInterfaceText("Retenção")}</span>
      <span className="px-2 py-0.5 text-[10px] font-medium text-gray-400 hover:text-gray-600 cursor-default">
        {translateInterfaceText("Origem")}</span>
    </div>
  );
}

// Chart tooltip component
interface TooltipProps {
  data: typeof CHART_DATA[0];
  position: { x: number; y: number };
  visible: boolean;
  pointIndex: number;
  totalPoints: number;
  chartHeight: number;
}

function ChartTooltip({ data, position, visible, pointIndex, totalPoints, chartHeight }: TooltipProps) {
  useLanguage();
  if (!visible) return null;

  // Smart positioning to prevent cutoff
  const tooltipHeight = 130; // approximate tooltip height
  const safePadding = 14;
  
  // If point is near top, position tooltip BELOW the point
  const shouldPositionBelow = position.y < (tooltipHeight + safePadding);
  
  const getTransform = () => {
    // Vertical positioning
    const verticalTransform = shouldPositionBelow 
      ? 'translateY(16px)' 
      : 'translateY(-100%) translateY(-12px)';
    
    // Horizontal positioning based on point position
    if (pointIndex === 0) {
      return `translateX(0) ${verticalTransform}`;
    } else if (pointIndex === totalPoints - 1) {
      return `translateX(-100%) ${verticalTransform}`;
    }
    return `translateX(-50%) ${verticalTransform}`;
  };

  return (
    <div 
      className="absolute z-20 bg-white rounded-xl shadow-lg border border-gray-100 p-3 min-w-[140px] animate-tooltip-enter"
      style={{ 
        left: position.x,
        top: position.y,
        transform: getTransform()
      }}
      aria-hidden="true"
    >
      <p className="text-[11px] font-semibold text-gray-800 mb-2">{data.label}</p>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#1F6BFF]" />
            <span className="text-[10px] text-gray-500">{translateInterfaceText("Totais")}</span>
          </div>
          <span className="text-[10px] font-medium text-gray-700">{data.totais}</span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#1F6BFF]/55" />
            <span className="text-[10px] text-gray-500">{translateInterfaceText("Únicas")}</span>
          </div>
          <span className="text-[10px] font-medium text-gray-700">
            {data.unicas ?? "—"}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#1F6BFF]/35" />
            <span className="text-[10px] text-gray-500">{translateInterfaceText("Play Rate")}</span>
          </div>
          <span className="text-[10px] font-medium text-gray-700">{data.playRate}%</span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#1F6BFF]/20" />
            <span className="text-[10px] text-gray-500">{translateInterfaceText("Tempo")}</span>
          </div>
          <span className="text-[10px] font-medium text-gray-700">{data.tempo}s</span>
        </div>
      </div>
    </div>
  );
}

// Fake cursor SVG
function FakeCursor({ x, y, visible }: { x: number; y: number; visible: boolean }) {
  useLanguage();
  return (
    <div 
      className={cn(
        "absolute z-30 pointer-events-none transition-all duration-300 will-change-transform",
        visible ? "opacity-100" : "opacity-0"
      )}
      style={{ 
        left: x,
        top: y,
        transform: 'translate(-2px, -2px)'
      }}
      aria-hidden="true"
    >
      <svg width="20" height="26" viewBox="0 0 20 26" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path 
          d="M1 1L1 19.5L5.5 15L10.5 24L13.5 22.5L8.5 13.5L15 13.5L1 1Z" 
          fill="white" 
          stroke="#1F2937" 
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

// Line chart component
interface LineChartProps {
  activePoint: number;
  onPointPositions: (positions: { x: number; y: number }[]) => void;
}

function LineChart({ activePoint, onPointPositions }: LineChartProps) {
  useLanguage();
  const chartWidth = 320;
  const chartHeight = 110;
  const paddingX = 15;
  const paddingY = 10;
  
  // Calculate point positions
  const points = useMemo(() => {
    const maxValue = Math.max(...CHART_DATA.map(d => d.totais));
    const minValue = Math.min(...CHART_DATA.map(d => d.totais));
    const range = maxValue - minValue || 1;
    
    return CHART_DATA.map((d, i) => ({
      x: paddingX + (i / (CHART_DATA.length - 1)) * (chartWidth - paddingX * 2),
      y: paddingY + (1 - (d.totais - minValue) / range) * (chartHeight - paddingY * 2),
      data: d
    }));
  }, []);

  // Report positions to parent
  useEffect(() => {
    onPointPositions(points.map(p => ({ x: p.x, y: p.y })));
  }, [points, onPointPositions]);

  // Create smooth path
  const pathD = useMemo(() => {
    if (points.length < 2) return "";
    
    let d = `M ${points[0].x} ${points[0].y}`;
    
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const cpx = (prev.x + curr.x) / 2;
      d += ` Q ${prev.x + (curr.x - prev.x) * 0.5} ${prev.y}, ${cpx} ${(prev.y + curr.y) / 2}`;
      d += ` T ${curr.x} ${curr.y}`;
    }
    
    return d;
  }, [points]);

  // Area path (for gradient fill)
  const areaD = useMemo(() => {
    if (points.length < 2) return "";
    return `${pathD} L ${points[points.length - 1].x} ${chartHeight} L ${points[0].x} ${chartHeight} Z`;
  }, [pathD, points, chartHeight]);

  return (
    <div className="relative">
      {/* Chart scan effect */}
      <div 
        className="absolute inset-0 overflow-hidden pointer-events-none"
        aria-hidden="true"
      >
        <div className="absolute inset-0 animate-chart-scan bg-gradient-to-r from-transparent via-white/5 to-transparent" />
      </div>

      <svg 
        width={chartWidth} 
        height={chartHeight + 25} 
        viewBox={`0 0 ${chartWidth} ${chartHeight + 25}`}
        className="overflow-visible"
      >
        <defs>
          <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#1F6BFF" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#1F6BFF" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Area fill */}
        <path d={areaD} fill="url(#areaGradient)" />

        {/* Line */}
        <path 
          d={pathD} 
          fill="none" 
          stroke="#1F6BFF" 
          strokeWidth="2.5" 
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Vertical dashed line for active point */}
        {activePoint >= 0 && (
          <line
            x1={points[activePoint].x}
            y1={points[activePoint].y}
            x2={points[activePoint].x}
            y2={chartHeight}
            stroke="#CBD5E1"
            strokeWidth="1"
            strokeDasharray="3 3"
            className="transition-opacity duration-200"
          />
        )}

        {/* Points */}
        {points.map((point, i) => (
          <g key={i}>
            <circle
              cx={point.x}
              cy={point.y}
              r={activePoint === i ? 6 : 4}
              fill="#1F6BFF"
              stroke="white"
              strokeWidth="2"
              className={cn(
                "transition-all duration-200",
                activePoint === i && "animate-point-glow"
              )}
            />
          </g>
        ))}

        {/* X-axis labels */}
        {points.map((point, i) => (
          <text
            key={i}
            x={point.x}
            y={chartHeight + 18}
            textAnchor="middle"
            className="text-[9px] fill-gray-400"
          >
            {point.data.date}
          </text>
        ))}
      </svg>
    </div>
  );
}

// KPI cards component
interface KPICardsProps {
  kpiState: typeof KPI_STATES[0];
  trigger: number;
  compact?: boolean;
}

function KPICards({ kpiState, trigger, compact }: KPICardsProps) {
  useLanguage();
  const views = useCountUp(kpiState.visualizacoes, 400, trigger);
  const uniques = useCountUp(kpiState.unicas, 400, trigger);
  const playRate = useCountUp(kpiState.playRate, 400, trigger);

  const cards = [
    { icon: Eye, label: translateInterfaceText("Visualizações"), value: formatNumber(views) },
    { icon: User, label: translateInterfaceText("Vis. Únicas"), value: formatNumber(uniques) },
    { icon: Play, label: translateInterfaceText("Play Rate"), value: `${playRate.toFixed(1)}%` },
  ];

  const displayCards = compact ? cards.slice(0, 2) : cards;

  return (
    <div className={cn(
      "grid gap-2 mt-3",
      compact ? "grid-cols-2" : "grid-cols-3"
    )}>
      {displayCards.map((card, i) => (
        <div 
          key={i}
          className="bg-gray-50 rounded-lg p-2 border border-gray-100"
        >
          <div className="flex items-center gap-1 mb-0.5">
            <card.icon className="w-3 h-3 text-gray-400" />
            <span className="text-[9px] text-gray-500 uppercase tracking-wide">{card.label}</span>
          </div>
          <p className="text-[15px] font-bold text-gray-800">{card.value}</p>
        </div>
      ))}
    </div>
  );
}

export function MetricsScene({ isActive }: MetricsSceneProps) {
  useLanguage();
  const isMobile = useIsMobile();
  const [activePoint, setActivePoint] = useState(-1);
  const [pointPositions, setPointPositions] = useState<{ x: number; y: number }[]>([]);
  const [cursorPos, setCursorPos] = useState({ x: -30, y: 50 });
  const [cursorVisible, setCursorVisible] = useState(false);
  const [trigger, setTrigger] = useState(0);

  // Animation loop - only 2 stages
  useEffect(() => {
    if (!isActive) {
      setActivePoint(-1);
      setCursorVisible(false);
      setCursorPos({ x: -30, y: 50 });
      return;
    }

    let animationIndex = 0;
    let timeoutId: NodeJS.Timeout;

    const animateToStage = (stageIndex: number) => {
      if (stageIndex >= ANIMATION_POINTS.length) {
        // End of loop - fade out cursor and reset
        setCursorVisible(false);
        setActivePoint(-1);
        
        timeoutId = setTimeout(() => {
          animationIndex = 0;
          const firstPoint = ANIMATION_POINTS[0];
          setCursorPos({ x: -30, y: pointPositions[firstPoint]?.y ?? 50 });
          setTrigger(t => t + 1);
          animateToStage(0);
        }, LOOP_DELAY);
        return;
      }

      const pointIndex = ANIMATION_POINTS[stageIndex];
      const pos = pointPositions[pointIndex];
      if (!pos) return;

      // Move cursor to point
      setCursorPos({ x: pos.x + 8, y: pos.y - 5 });
      setCursorVisible(true);

      // After move animation, activate point
      timeoutId = setTimeout(() => {
        setActivePoint(pointIndex);
        setTrigger(t => t + 1);

        // Pause on point, then move to next stage
        timeoutId = setTimeout(() => {
          animationIndex = stageIndex + 1;
          animateToStage(animationIndex);
        }, POINT_PAUSE);
      }, MOVE_DURATION);
    };

    // Start immediately with first animation point (no initial delay)
    if (pointPositions.length > 0) {
      const firstPoint = ANIMATION_POINTS[0];
      setCursorPos({ x: -30, y: pointPositions[firstPoint]?.y ?? 50 });
      timeoutId = setTimeout(() => animateToStage(0), 100);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isActive, pointPositions]);

  const currentKPI = activePoint >= 0 ? KPI_STATES[activePoint] : KPI_STATES[0];
  const currentData = activePoint >= 0 ? CHART_DATA[activePoint] : CHART_DATA[0];
  const tooltipPos = activePoint >= 0 && pointPositions[activePoint] 
    ? { x: pointPositions[activePoint].x, y: pointPositions[activePoint].y }
    : { x: 0, y: 0 };

  return (
    <div className="flex flex-col h-full px-5 py-3 relative">
      {/* Mini tabs */}
      <MiniTabs />
      
      {/* Title */}
      <h3 className="text-[13px] font-semibold text-gray-800 mb-2">
        {translateInterfaceText("Desempenho diário")}</h3>

      {/* Chart container */}
      <div className="relative flex-1 flex items-center justify-center">
        <LineChart 
          activePoint={activePoint} 
          onPointPositions={setPointPositions}
        />
        
        {/* Tooltip */}
        <ChartTooltip 
          data={currentData}
          position={tooltipPos}
          visible={activePoint >= 0}
          pointIndex={activePoint}
          totalPoints={CHART_DATA.length}
          chartHeight={110}
        />

        {/* Fake cursor (desktop only) */}
        {!isMobile && (
          <FakeCursor 
            x={cursorPos.x}
            y={cursorPos.y}
            visible={cursorVisible}
          />
        )}

        {/* Mobile: animated vertical line instead of cursor */}
        {isMobile && activePoint >= 0 && pointPositions[activePoint] && (
          <div 
            className="absolute w-0.5 bg-[#1F6BFF]/30 transition-all duration-300"
            style={{
              left: pointPositions[activePoint].x,
              top: 0,
              height: pointPositions[activePoint].y,
            }}
            aria-hidden="true"
          />
        )}
      </div>

      {/* KPI cards */}
      <KPICards 
        kpiState={currentKPI} 
        trigger={trigger}
        compact={isMobile}
      />
    </div>
  );
}
